/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  ANSMetadata,
  ANSMetadataEntry,
  Metadata,
  Notification,
  UNSMetadataEntry,
  User,
  UserProfile
} from './model/Request';
import {MessageTypes} from './model/MessageType';

export default class Utils {
  static getANSDefaultHeaders(accessToken: string) {
    return {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-adobe-app-id': 'ExcPulse',
      'x-api-key': 'exc-pulse'
    };
  }

  static getUNSDefaultHeaders(accessToken: string, imsOrgId: string) {
    return {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-api-key': 'exc_app',
      'x-gw-ims-org-id': `${imsOrgId}`
    };
  }

  static constructUserInfo(userProfile: UserProfile): User {
    return {
      firstName: userProfile.first_name,
      lastName: userProfile.last_name,
      avatar: userProfile.avatar
    };
  }

  static constructMetadata(metadata: Metadata | undefined) {
    const ansMetadata: ANSMetadata = {entry: []};

    for (const keyName in metadata) {
      const value = metadata[keyName];
      let ansMetadatEntry: ANSMetadataEntry;
      ansMetadatEntry = Object.assign({'key-name': keyName, 'key-value': value});
      ansMetadata.entry.push(ansMetadatEntry);
    }
    return ansMetadata;
  }

  static constructUNSMetadata(metadata: Metadata | undefined) {
    const unsMetadata: any = [];

    for (const keyName in metadata) {
      const value = metadata[keyName];
      let unsMetadataEntry: UNSMetadataEntry;
      unsMetadataEntry = Object.assign({'key-name': keyName, 'key-value': value});
      unsMetadata.push(unsMetadataEntry);
    }
    return unsMetadata;
  }

  static validateBaseParams(data: Notification[], imsOrgId: string, method: string) {
    if (!data || data.length == 0) {
      return {error: `Invalid Pulse ${method} request : Payload is empty`};
    }

    if (!imsOrgId || imsOrgId.length == 0) {
      return {error: `Invalid Pulse ${method} request : IMS Org is empty`};
    }

    return {};
  }

  static validateUNSRequest(notification: Notification, method: string) {
    if (!notification) {
      return {error: `Invalid Pulse ${method} request : notification is empty`};
    }

    if (!notification.messageType) {
      return {error: `Invalid Pulse ${method} request : Message Type is empty`};
    }

    if (!this.isMessageTypeSupported(notification.messageType)) {
      return {error: `Invalid Pulse ${method} request : Message Type is not supported`};
    }
    return {};
  }

  private static isMessageTypeSupported(messageType: string): boolean {
    switch (messageType) {
      case MessageTypes.ASSIGNED:
      case MessageTypes.SHARED:
      case MessageTypes.OTHERS:
      case MessageTypes.MENTIONS:
        return true;
      default:
        return false;
    }
  }
}
